import get from 'lodash/get';
import startsWith from 'lodash/startsWith';
import React from 'react';

import { formatPhoneNumber, formatPhoneNumberAsYouType } from '../formatters';

import { updateCaretPosition } from './DOM';

function computeNewtCaretPosition(
  initialPosition: number,
  oldValue: string,
  newValue: string,
): number {
  const leadingDoubleZeros = startsWith(oldValue, '00');

  let indexOld = leadingDoubleZeros ? 2 : 0;

  let indexNew = leadingDoubleZeros ? 1 : 0;

  while (indexOld < initialPosition && indexNew < newValue.length) {
    indexOld += get(
      oldValue.slice(indexOld).match(/[0-9]/),
      'index',
      oldValue.length - indexOld,
    );
    indexNew += get(
      newValue.slice(indexNew).match(/[0-9]/),
      'index',
      newValue.length - indexNew,
    );
    if (
      oldValue[indexOld] !== newValue[indexNew] ||
      indexOld >= initialPosition
    ) {
      return indexNew;
    }
    indexOld++;
    indexNew++;
  }

  return indexNew;
}

interface PhoneNumberChangeData {
  name?: string;
  value: string;
  [key: string]: unknown;
}

interface PhoneNumberInputProps {
  onChange: (
    event: React.SyntheticEvent<HTMLInputElement>,
    data: PhoneNumberChangeData,
  ) => void;
  onBlur?: () => void;
  value: string;
  countryCode: string;
  children: (
    value: string,
    handleValueChange: (
      event: React.ChangeEvent<HTMLInputElement>,
      data: PhoneNumberChangeData,
    ) => void,
    handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void,
  ) => React.ReactNode;
}

export class PhoneNumberInput extends React.Component<PhoneNumberInputProps> {
  private readonly handleValueChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    data: PhoneNumberChangeData,
  ) => void;

  private readonly handleBlur: (
    event: React.FocusEvent<HTMLInputElement>,
  ) => void;

  constructor(props: PhoneNumberInputProps) {
    super(props);

    this.handleValueChange = (e, data) => {
      e.preventDefault();
      const { onChange, countryCode } = this.props;
      const element = e.target;
      const phoneNumber = e.target.value;
      const formattedValue = formatPhoneNumberAsYouType(
        phoneNumber,
        countryCode,
      );
      const caretPosition = computeNewtCaretPosition(
        element.selectionStart ?? 0,
        phoneNumber,
        formattedValue,
      );

      e.target.value = formattedValue;
      onChange(e, { ...data, value: formattedValue });
      window.setTimeout(() => {
        updateCaretPosition(element, caretPosition);
      });
    };

    this.handleBlur = (e) => {
      e.preventDefault();
      const { onBlur, onChange, value, countryCode } = this.props;
      const element = e.currentTarget;
      const formattedValue = formatPhoneNumber(value, countryCode);

      if (formattedValue !== value) {
        element.value = formattedValue ?? '';
        onChange(e, { name: element.name, value: formattedValue ?? '' });
      }
      if (onBlur) {
        onBlur();
      }
    };
  }

  render() {
    const { children, value } = this.props;

    return children(value, this.handleValueChange, this.handleBlur);
  }
}
